/**
 * Preset mission-area derivation: one rectangular AO buffered around each of
 * the unit's base locations. Shared by the first-run mission-area setup step
 * and the admin mission-areas editor ("Add preset areas"), so both offer the
 * exact same presets. Presets are named after the base location itself,
 * matching the seeded-area convention, so re-adding never duplicates rows.
 */

import type { AreaGeometry, MissionArea, UnitLocation } from '@vtak/shared';

/** Latitude/longitude degrees buffered around a base to form its preset AO. */
export const PRESET_BUFFER_DEGREES = 0.03;

/** A derivable preset area, not yet persisted. */
export interface AreaPreset {
  /** Display name (the base location's name) */
  name: string;

  /** Rectangular polygon centered on the base location */
  geometry: AreaGeometry;
}

/** Rectangle polygon (NW → NE → SE → SW) buffered around a point. */
function bufferRectangle(lat: number, lng: number, buffer: number): AreaGeometry {
  return {
    shapeType: 'polygon',
    vertices: [
      { lat: lat + buffer, lon: lng - buffer },
      { lat: lat + buffer, lon: lng + buffer },
      { lat: lat - buffer, lon: lng + buffer },
      { lat: lat - buffer, lon: lng - buffer },
    ],
  };
}

/** Derive one preset AO per base location (kind 'base'), in given order. */
export function derivePresetAreas(locations: UnitLocation[]): AreaPreset[] {
  return locations
    .filter((location) => location.kind === 'base')
    .map((location) => ({
      name: location.name,
      geometry: bufferRectangle(location.lat, location.lng, PRESET_BUFFER_DEGREES),
    }));
}

/** Presets whose names don't already exist among the unit's shared areas. */
export function missingPresetAreas(presets: AreaPreset[], areas: MissionArea[]): AreaPreset[] {
  const existing = new Set(sharedAreas(areas).map((area) => area.name));
  return presets.filter((preset) => !existing.has(preset.name));
}

/** Unit-shared rows (no owner) among the caller's visible areas. */
export function sharedAreas(areas: MissionArea[]): MissionArea[] {
  return areas.filter((area) => area.ownerSub === null);
}

/** Shared mission AOIs ('area' kind) — the rows the first-run setup checks for. */
export function sharedMissionAois(areas: MissionArea[]): MissionArea[] {
  return sharedAreas(areas).filter((area) => area.kind === 'area');
}
